import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { trackOperation } from './trackOperation';

export function useTrackedMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown
>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    'mutationFn'
  >
): UseMutationResult<TData, TError, TVariables, TContext> {
  // Create a mutation name from options if available
  let mutationName: string;

  if (options?.mutationKey) {
    // Use the mutationKey if available
    mutationName = Array.isArray(options.mutationKey)
      ? options.mutationKey.join(':')
      : String(options.mutationKey);
  } else {
    try {
      const stackTrace = new Error().stack || '';
      const callerLine = stackTrace.split('\n')[2] || ''; // Get the caller's line
      const callerMatch = callerLine.match(/at\s+(\w+)\s+\(/);
      mutationName = callerMatch ? callerMatch[1] : 'mutation';
    } catch (e) {
      mutationName = 'unknown';
    }
  }

  // Wrap the mutationFn with our generic Sentry span tracker
  const trackedMutationFn = trackOperation(
    mutationFn,
    `mutation:${mutationName}`,
    'mutation'
  );

  // Use the original useMutation with our wrapped function
  return useMutation<TData, TError, TVariables, TContext>(
    trackedMutationFn,
    options
  );
}
